import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { API } from "../App";
import { useTranslation } from "react-i18next";
import Header from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { UserPlus, Loader2, AlertTriangle, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

export default function AcceptInvitationPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [inviteData, setInviteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ full_name: "", password: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) { setError("No invitation token provided"); setLoading(false); return; }
    const validate = async () => {
      try {
        const res = await axios.get(`${API}/auth/invitation/${token}`);
        setInviteData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Invalid or expired invitation");
      } finally { setLoading(false); }
    };
    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/accept-invitation`, { token, ...form });
      setSuccess(true);
      toast.success("Account created successfully!");
      setTimeout(() => {
        if (inviteData?.role === "venue_owner" || inviteData?.role === "venue_agent") {
          navigate("/owner");
        } else if (inviteData?.role === "admin") {
          navigate("/admin");
        } else {
          navigate("/");
        }
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept invitation");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-20 flex items-center justify-center min-h-screen">
        <div className="w-full max-w-md px-4">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Validating invitation...</p>
            </div>
          ) : error ? (
            <div className="bg-card rounded-xl border border-border p-8 text-center">
              <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
              <h2 className="text-xl font-medium text-foreground mb-2">{t("accept_invite:invalid")}</h2>
              <p className="text-muted-foreground mb-6">{error}</p>
              <Button variant="outline" onClick={() => navigate("/login")}>Go to Login</Button>
            </div>
          ) : success ? (
            <div className="bg-card rounded-xl border border-border p-8 text-center" data-testid="invitation-success">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-emerald-600" />
              </div>
              <h2 className="text-xl font-medium text-foreground mb-2">Welcome to TableCy!</h2>
              <p className="text-muted-foreground">Your account has been created. Redirecting...</p>
            </div>
          ) : (
            <div className="bg-card rounded-xl border border-border p-8" data-testid="accept-invitation-form">
              <div className="text-center mb-6">
                <UserPlus className="h-10 w-10 text-primary mx-auto mb-3" />
                <h2 className="text-xl font-medium text-foreground">{t("accept_invite:title")}</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  You've been invited as <strong className="text-foreground">{inviteData?.role?.replace("_", " ")}</strong>
                </p>
                <p className="text-sm text-muted-foreground">{inviteData?.email}</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>{t("accept_invite:full_name")} *</Label>
                  <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required className="mt-1" data-testid="invite-accept-name" />
                </div>
                <div>
                  <Label>{t("accept_invite:password")} *</Label>
                  <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} className="mt-1" data-testid="invite-accept-password" />
                </div>
                <div>
                  <Label>{t("accept_invite:phone")}</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" data-testid="invite-accept-phone" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full" data-testid="invite-accept-submit">
                  {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  {t("accept_invite:submit")}
                </Button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
